import type { BadgeColor } from 'app/components/ui/badge'
import type { SmsAgentOpsCounts, SmsSender } from 'app/types/sms-agent-ops'

export const SENDERS: SmsSender[] = [`agent`, `workflow`, `staff`, `external`, `voiceAgent`]

export const SENDER_LABEL: Record<SmsSender, string> = {
  agent: `AI Agent`,
  workflow: `Workflow`,
  staff: `Staff`,
  external: `CallRail`,
  voiceAgent: `Voice Agent`,
}

export const SENDER_COLOR: Record<SmsSender, BadgeColor> = {
  agent: `purple`,
  workflow: `blue`,
  staff: `green`,
  external: `gray`,
  voiceAgent: `sky`,
}

export const SENDER_COUNT_KEY: Record<SmsSender, keyof SmsAgentOpsCounts> = {
  agent: `agentTexts`,
  workflow: `workflowTexts`,
  staff: `staffTexts`,
  external: `externalTexts`,
  voiceAgent: `voiceAgentTexts`,
}

/**
 * Who sent a text. New messages carry a stamped `source` set at create time (TRK-6031), so
 * read that directly. Inbound patient texts have `source: 'patient'` and no sender badge.
 * Messages created before the `source` field existed fall back to the old guess from the
 * `Message` fields: `isAgent` covers four senders, so the workflow ids tell them apart — a
 * workflow send (including the AI opener) carries `workflowRunId`, an AI reply only
 * `aiWorkflowId`, and the voice agent's send_sms tool neither.
 */
export function senderFor(message: {
  source?: string | null
  direction?: string | null
  sentByUserId?: string | null
  isAgent?: boolean | null
  workflowRunId?: string | null
  aiWorkflowId?: string | null
}): SmsSender | undefined {
  if (message.source) {
    return message.source === `patient` ? undefined : (message.source as SmsSender)
  }
  if (message.sentByUserId === `external`) return `external`
  if (message.sentByUserId) return `staff`
  if (!message.isAgent) return undefined
  if (message.workflowRunId) return `workflow`
  if (message.aiWorkflowId) return `agent`
  return `voiceAgent`
}
